
import { Badge, Group, Image, Modal, Stack, Text } from '@mantine/core'
import { IconCheck, IconDownload, IconEye, IconPlayerPlay } from '@tabler/icons-react'
import { MRT_ColumnDef } from 'mantine-react-table'
import { memo, useState } from 'react'
import BookPageList from 'src/containers/Book'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'

export interface Chapter {
	id: string
	title: string
	description: string
	cover_image: string
	original_doc_url: string | null
	audio_url: string | null
	approved_status?: string
	published?: boolean
}

const getStatusStyles = (status: string) => {
	const normalizedStatus = (status || '').toLowerCase()

	if (normalizedStatus === 'approved') {
		return {
			label: 'Approved',
			textColor: '#1f7a44',
			background: '#ecfdf3',
			borderColor: '#b7ebcb',
		}
	}

	if (normalizedStatus === 'inprogress') {
		return {
			label: 'In Progress',
			textColor: '#a16207',
			background: '#fff7ed',
			borderColor: '#fed7aa',
		}
	}

	if (normalizedStatus === 'rejected' || normalizedStatus === 'reject') {
		return {
			label: 'Rejected',
			textColor: '#b42318',
			background: '#fff1f3',
			borderColor: '#ffc7ce',
		}
	}

	return {
		label: normalizedStatus ? status : 'Rejected',
		textColor: '#b42318',
		background: '#fff1f3',
		borderColor: '#ffc7ce',
	}
}

const getBookStatusFromChapters = (rowData: any): string => {
	const chapters = Array.isArray(rowData?.chapters) ? rowData.chapters : []
	const chapterStatuses = chapters
		.map((chapter: Chapter) => String(chapter?.approved_status || '').toLowerCase())
		.filter(Boolean)

	if (chapterStatuses.includes('approved')) {
		return 'approved'
	}

	if (chapterStatuses.includes('rejected') || chapterStatuses.includes('reject')) {
		return 'rejected'
	}

	if (chapterStatuses.length > 0) {
		return 'inprogress'
	}

	return String(rowData?.status || rowData?.approved_status || 'inprogress').toLowerCase()
}

const renderBinaryBadge = (value: boolean) => (
	<Badge
		radius="sm"
		variant="light"
		size="md"
		sx={{
			paddingInline: 10,
			height: 26,
			fontWeight: 600,
			textTransform: 'none',
			background: value ? '#ecfdf3' : '#fff1f3',
			color: value ? '#1f7a44' : '#b42318',
			border: `1px solid ${value ? '#b7ebcb' : '#ffc7ce'}`,
		}}
	>
		{value ? 'Yes' : 'No'}
	</Badge>
)

const resolveAudioState = (value: unknown, row: any) => {
	if (typeof value === 'boolean') {
		return value
	}

	if (typeof value === 'string') {
		return value.trim().length > 0
	}

	if (Array.isArray(row?.chapters)) {
		return row.chapters.some((chapter: Chapter) => Boolean(chapter.audio_url))
	}

	return false
}

const renderFileActions = (
	originalUrl: string | null,
	handleViewFile: (url: string) => void,
	handleDownloadFile: (url: string) => void,
) => {
	if (!originalUrl) {
		return <Text color="#9ca3af">-</Text>
	}

	return (
		<Stack spacing={6}>
			<button
				type="button"
				onClick={() => handleViewFile(originalUrl)}
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: 6,
					cursor: 'pointer',
					minHeight: 30,
					padding: '6px 12px',
					borderRadius: 8,
					border: '1px solid #dbe6dc',
					background: '#f8fbf8',
					color: '#195c34',
					fontWeight: 600,
					fontSize: 13,
				}}
			>
				<IconEye size={14} /> View
			</button>
			<button
				type="button"
				onClick={() => handleDownloadFile(originalUrl)}
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: 6,
					cursor: 'pointer',
					minHeight: 30,
					padding: '6px 12px',
					borderRadius: 8,
					border: '1px solid #dbe6dc',
					background: '#ffffff',
					color: '#2a8a4d',
					fontWeight: 600,
					fontSize: 13,
				}}
			>
				<IconDownload size={14} /> Download
			</button>
		</Stack>
	)
}

function BooksPage() {
	const [toast] = useState<{
		visible: boolean
		type: 'success' | 'error'
		message: string
	}>({
		visible: false,
		type: 'success',
		message: '',
	})
	const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null)

	const getViewableUrl = (url: string) => {
		const isOfficeDocument = /\.(docx?|xlsx?|pptx?)(\?|#|$)/i.test(url)
		if (isOfficeDocument) {
			return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`
		}

		return url
	}

	const handleDownloadFile = (url: string) => {
		const link = document.createElement('a')
		link.href = url
		link.target = '_blank'
		link.rel = 'noreferrer'
		link.download = ''
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}

	const handleViewFile = (url: string) => {
		window.open(getViewableUrl(url), '_blank', 'noopener,noreferrer')
	}

	const handleOpenAudioPopup = (url: string) => {
		setAudioPreviewUrl(url)
	}

	const renderAudioPopupTrigger = (audioUrl: string) => {
		return (
			<button
				type="button"
				onClick={() => handleOpenAudioPopup(audioUrl)}
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: 6,
					cursor: 'pointer',
					minHeight: 30,
					padding: '6px 12px',
					borderRadius: 8,
					border: '1px solid #dbe6dc',
					background: '#f8fbf8',
					color: '#195c34',
					fontWeight: 600,
					fontSize: 13,
				}}
			>
				<IconPlayerPlay size={14} /> Play
			</button>
		)
	}

	const columns: MRT_ColumnDef[] = [
		{
			accessorKey: 'no',
			header: 'No',
			size: 60,
		},
		{
			accessorKey: 'title',
			header: 'Title & Content',
			size: 320,
			Cell: ({ row }) => {
				const rowData = row.original as any
				const cover = rowData?.cover_image
				const title = rowData?.title || '-'
				const summary = rowData?.summary || rowData?.description || '-'

				return (
					<Group spacing={12} noWrap>
						<Image
							src={cover || ''}
							width={54}
							height={74}
							radius={8}
							fit="cover"
							withPlaceholder
						/>
						<Stack spacing={4} sx={{ minWidth: 0 }}>
							<Text fw={700} sx={{ lineHeight: 1.35 }} lineClamp={1}>
								{title}
							</Text>
							<Text color="dimmed" size="sm" sx={{ lineHeight: 1.4 }} lineClamp={2}>
								{summary}
							</Text>
						</Stack>
					</Group>
				)
			},
		},
		{
			accessorKey: 'author',
			header: 'Author',
			size: 160,
			Cell: ({ cell }) => {
				return (
					<Text lineClamp={2}>{cell.getValue<string>() || '-'}</Text>
				)
			},
		},
		{
			accessorKey: 'category',
			header: 'Category',
			size: 170,
			Cell: ({ cell }) => {
				const value = cell.getValue<string[]>()
				if (!value?.length) {
					return <Text>-</Text>
				}

				return (
					<Stack spacing={2}>
						{value.map((item: string, index: number) => (
							<Text key={`${item}-${index}`} size="sm" sx={{ lineHeight: 1.45 }}>
								{item}
							</Text>
						))}
					</Stack>
				)
			},
		},
		{
			accessorKey: 'summary',
			header: 'Summary',
			size: 220,
			Cell: ({ cell }) => {
				return <Text lineClamp={3}>{cell.getValue<string>() || '-'}</Text>
			},
		},
		{
			accessorKey: 'chapters',
			header: 'Total Chapter',
			size: 90,
			Cell: ({ cell }) => {
				const chapters = cell.getValue<Chapter[]>() ?? []
				return (
					<Text fw={600}>{chapters.length || '-'}</Text>
				)
			},
		},
		{
			accessorKey: 'published',
			header: 'Published',
			size: 90,
			Cell: ({ cell }) => {
				return renderBinaryBadge(Boolean(cell.getValue<boolean>()))
			},
		},
		{
			accessorKey: 'audio_url',
			header: 'Audio',
			size: 80,
			Cell: ({ cell, row }) => {
				const audioUrl = cell.getValue<string>()
				if (typeof audioUrl === 'string' && audioUrl.trim()) {
					return renderAudioPopupTrigger(audioUrl)
				}

				const hasAudio = resolveAudioState(cell.getValue<unknown>(), row.original)
				return renderBinaryBadge(hasAudio)
			},
		},
		{
			accessorKey: 'original_doc_url',
			header: 'Doc File',
			size: 160,
			Cell: ({ cell }) => {
				return renderFileActions(cell.getValue<string | null>(), handleViewFile, handleDownloadFile)
			},
		},
		{
			accessorKey: 'updated_at',
			header: 'Updated At',
			size: 120,
		},
		{
			accessorKey: 'approved_status',
			header: 'Status',
			size: 120,
			Cell: ({ cell, row }) => {
				const rowData = row.original as any
				const rawStatus = getBookStatusFromChapters(rowData)
				const styles = getStatusStyles(rawStatus)

				return (
					<Badge
						radius="xl"
						variant="light"
						leftSection={<IconCheck size={12} />}
						sx={{
							paddingInline: 12,
							height: 30,
							fontWeight: 600,
							textTransform: 'none',
							background: styles.background,
							color: styles.textColor,
							border: `1px solid ${styles.borderColor}`,
						}}
					>
						{styles.label}
					</Badge>
				)
			},
		},
	]
	const fields = []

	const chapterColumns: MRT_ColumnDef[] = [
		{
			accessorKey: 'title',
			header: 'Title & Content',
			size: 340,
			Cell: ({ row }) => {
				const rowData = row.original as any
				const cover = rowData?.cover_image
				const title = rowData?.title || '-'
				const description = rowData?.description || '-'

				return (
					<Group spacing={12} noWrap>
						<Image
							src={cover || ''}
							width={40}
							height={56}
							radius={6}
							fit="cover"
							withPlaceholder
						/>
						<Stack spacing={3} sx={{ minWidth: 0 }}>
							<Text fw={700} size="sm" lineClamp={1}>
								{title}
							</Text>
							<Text color="dimmed" size="sm" lineClamp={2}>
								{description}
							</Text>
						</Stack>
					</Group>
				)
			},
		},
		{
			accessorKey: 'author',
			header: 'Author',
			size: 150,
			Cell: ({ cell }) => {
				return <Text>{cell.getValue<string>() || 'UniVini Team'}</Text>
			},
		},
		{
			accessorKey: 'category',
			header: 'Category',
			size: 160,
			Cell: ({ cell }) => {
				const value = cell.getValue<string[]>()
				if (!value?.length) {
					return <Text>-</Text>
				}

				return (
					<Stack spacing={2}>
						{value.map((item: string, index: number) => (
							<Text key={`${item}-${index}`} size="sm" sx={{ lineHeight: 1.45 }}>
								{item}
							</Text>
						))}
					</Stack>
				)
			},
		},
		{
			accessorKey: 'description',
			header: 'Summary',
			size: 220,
			Cell: ({ cell }) => {
				return <Text lineClamp={3}>{cell.getValue<string>() || '-'}</Text>
			},
		},
		{
			accessorKey: 'published',
			header: 'Published',
			size: 90,
			Cell: ({ cell }) => {
				return renderBinaryBadge(Boolean(cell.getValue<boolean>()))
			},
		},
		{
			accessorKey: 'audio_url',
			header: 'Audio',
			size: 80,
			Cell: ({ cell, row }) => {
				const audioUrl = cell.getValue<string>()
				if (typeof audioUrl === 'string' && audioUrl.trim()) {
					return renderAudioPopupTrigger(audioUrl)
				}

				const hasAudio = resolveAudioState(cell.getValue<unknown>(), row.original)
				return renderBinaryBadge(hasAudio)
			},
		},
		{
			accessorKey: 'original_doc_url',
			header: 'Doc File',
			size: 160,
			Cell: ({ cell }) => {
				return renderFileActions(cell.getValue<string | null>(), handleViewFile, handleDownloadFile)
			},
		},
		{
			accessorKey: 'updated_at',
			header: 'Updated At',
			size: 120,
		},
		{
			accessorKey: 'approved_status',
			header: 'Status',
			size: 120,
			Cell: ({ cell, row }) => {
				const rowData = row.original as any
				const rawStatus =
					(cell.getValue<string>() || rowData?.status || rowData?.approved_status || '').toLowerCase()
				const styles = getStatusStyles(rawStatus)

				return (
					<CustomTooltip label={styles.label}>
						<Badge
							radius="xl"
							variant="light"
							leftSection={<IconCheck size={12} />}
							sx={{
								paddingInline: 12,
								height: 30,
								fontWeight: 600,
								textTransform: 'none',
								background: styles.background,
								color: styles.textColor,
								border: `1px solid ${styles.borderColor}`,
							}}
						>
							{styles.label}
						</Badge>
					</CustomTooltip>
				)
			},
		},
	]
	return (
		<>
			<BookPageList columns={columns} fields={fields} chapterColumns={chapterColumns} />
			<Modal
				opened={Boolean(audioPreviewUrl)}
				onClose={() => setAudioPreviewUrl(null)}
				title="Audio Preview"
				centered
				size="lg"
			>
				{audioPreviewUrl ? (
					<audio
						controls
						autoPlay
						preload="metadata"
						src={audioPreviewUrl}
						style={{ width: '100%' }}
					>
						Your browser does not support the audio element.
					</audio>
				) : null}
			</Modal>
			{toast.visible && (
				<div
					style={{
						position: 'fixed',
						top: 20,
						right: 20,
						zIndex: 2000,
						padding: '10px 14px',
						borderRadius: 8,
						fontWeight: 600,
						color: '#fff',
						background: toast.type === 'success' ? '#16a34a' : '#dc2626',
						boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
					}}
				>
					{toast.message}
				</div>
			)}
		</>
	)
}

export default memo(BooksPage)
